import { assignInlineVars } from "@vanilla-extract/dynamic";
import { AgGrid } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DEF_FILTER_DEBOUNCE_MS, DELETED_DISTRIBUTION_LABEL } from "../../constants";
import { AccountFilter } from "../../GridFilters/AccountFilter";
import { ClientFilter } from "../../GridFilters/ClientFilter";
import { EntityFilter } from "../../GridFilters/EntityFilter";
import Money from "../../Money";
import { SequenceTextWithIcon } from "../../SequenceTextWithIcon";
import { TextWithIcon } from "../../TextWithIcon";
import { BatchStatusToolTip } from "../BatchStatusToolTip";
import {
  verticalCenterWrapper,
  batchStatusText,
  batchStatusColor,
  deletedBatchText,
} from "../styles_vanilla.css";
import { PaymentRowItem } from "../types";
import { batchStatusFilterTypes } from "../util/batchStatusUtil";
import { paymentStatusFilterTypes } from "../util/paymentStatusUtils";

/**
 * List of the supported columns in the Payment Batches table
 */
export enum SupportedColumns {
  DistributionName = "distributionName",
  CreatedDate = "createdDate",
  BatchStatus = "batchStatus",
  PaymentStatus = "paymentStatus",
  ClientName = "clientName",
  EntityName = "entityName",
  OriginatingAccount = "originatingAccount",
  ReceivingAccount = "receivingAccount",
  TotalAmount = "totalAmount",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.DistributionName, "Batch"],
  [SupportedColumns.CreatedDate, "Created date"],
  [SupportedColumns.BatchStatus, "Batch status"],
  [SupportedColumns.PaymentStatus, "Payments status"],
  [SupportedColumns.ClientName, "Client"],
  [SupportedColumns.EntityName, "Entity"],
  [SupportedColumns.OriginatingAccount, "Originating account"],
  [SupportedColumns.ReceivingAccount, "Receiving account"],
  [SupportedColumns.TotalAmount, "Amount"],
]);

/**
 * AG Grid, column definitions for the Payment Batches table
 */
export const columnDefinitions: AgGrid.ColDef[] = [
  {
    field: SupportedColumns.DistributionName,
    headerName: columnNames.get(SupportedColumns.DistributionName),
    sortable: false,
    cellRenderer: TextWithIcon,
    cellClass: verticalCenterWrapper,
    cellRendererParams: ({ data }: { data: PaymentRowItem }) => {
      const doesBatchExists = data.distributionName?.length || false;
      return {
        label: doesBatchExists ? data.distributionName : DELETED_DISTRIBUTION_LABEL,
        className: doesBatchExists ? undefined : deletedBatchText,
        iconType: doesBatchExists ? undefined : "exclamation-triangle",
        iconColor: doesBatchExists ? undefined : Colors.Danger600,
      };
    },
  },
  {
    field: SupportedColumns.CreatedDate,
    headerName: columnNames.get(SupportedColumns.CreatedDate),
    sortable: false,
    filter: "agDateColumnFilter",
    filterParams: {
      buttons: ["reset"],
      maxNumConditions: 1,
      debounceMs: DEF_FILTER_DEBOUNCE_MS,
      filterOptions: ["inRange"],
    },
  },
  {
    field: SupportedColumns.BatchStatus,
    headerName: columnNames.get(SupportedColumns.BatchStatus),
    sortable: true,
    tooltipComponent: BatchStatusToolTip,
    filter: "agSetColumnFilter",
    filterParams: {
      defaultToNothingSelected: true,
      debounceMs: DEF_FILTER_DEBOUNCE_MS,
      showTooltips: true,
      values: [...batchStatusFilterTypes.keys()],
    },
    valueFormatter: ({ data }: { data: PaymentRowItem }) => {
      return data?.batchStatusConfig?.label;
    },
    cellClass: batchStatusText,
    cellStyle: ({ data }: { data: PaymentRowItem }) =>
      assignInlineVars({ [batchStatusColor]: data?.batchStatusConfig?.labelColor }),
  },
  {
    field: SupportedColumns.PaymentStatus,
    headerName: columnNames.get(SupportedColumns.PaymentStatus),
    sortable: false,
    filter: "agSetColumnFilter",
    filterParams: {
      defaultToNothingSelected: true,
      debounceMs: DEF_FILTER_DEBOUNCE_MS,
      values: [...paymentStatusFilterTypes.keys()],
    },
    cellRenderer: SequenceTextWithIcon,
    cellClass: verticalCenterWrapper,
    cellRendererParams: ({ data }: { data: PaymentRowItem }) => ({
      items: data.paymentStatus,
    }),
  },
  {
    field: SupportedColumns.ClientName,
    headerName: columnNames.get(SupportedColumns.ClientName),
    sortable: false,
    filter: ClientFilter,
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.EntityName,
    headerName: columnNames.get(SupportedColumns.EntityName),
    sortable: false,
    filter: EntityFilter,
    filterParams: {
      dependentFilter: SupportedColumns.ClientName,
    },
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.OriginatingAccount,
    headerName: columnNames.get(SupportedColumns.OriginatingAccount),
    sortable: false,
  },
  {
    field: SupportedColumns.ReceivingAccount,
    headerName: columnNames.get(SupportedColumns.ReceivingAccount),
    sortable: false,
    filter: AccountFilter,
    filterParams: {
      dependentFilter: SupportedColumns.ClientName,
    },
    // Exclude filtering from the column header because UX isn't great
    menuTabs: ["generalMenuTab", "columnsMenuTab"],
  },
  {
    field: SupportedColumns.TotalAmount,
    headerName: columnNames.get(SupportedColumns.TotalAmount),
    sortable: true,
    filter: "agNumberColumnFilter",
    filterParams: {
      maxNumConditions: 1,
      debounceMs: DEF_FILTER_DEBOUNCE_MS,
      filterOptions: ["inRange", "greaterThan", "lessThan"],
    },
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: PaymentRowItem }) => ({
      money: data.totalAmount,
    }),
  },
];
